import { useEffect, useState, useRef, useCallback } from 'react'
import {
	getCurrentVocalTrackFromMission,
	getScreenFromMission,
} from '@mission.io/mission-toolkit'
import { useLocalDispatch, useLocalState } from './provider'
import { useMissionStateSelector } from './customHooks'
import { getAudioDuckingValue, updateAudioDuckingValue } from './reducer'
import {
	usePrevious,
	AUDIO_DUCKING_DELAY,
	AUDIO_DUCK_SOURCES,
} from '../utility'
import useInterval from '@use-it/interval'
import { COLLABORATIVE_CULMINATING_MOMENT } from '@mission.io/mission-toolkit/constants'
import type { AudioDuckSource } from '../utility/constants'

// delay for Audio ducking interval
const AUDIO_DUCKING_INTERVAL = 50
// How many times we *expect* the audio ducking interval to run
const audioDuckingIntervalExpectedRunCount =
	AUDIO_DUCKING_DELAY / AUDIO_DUCKING_INTERVAL
// How much the audio ducking value should change for each iteration of the interval

/**
 * A hook that allows us to duck the audio based on parameters passed in
 *
 * Adding an additional ducking source is as simple as adding a new key to the AUDIO_DUCK_SOURCES object in src/utility/constants and
 * creating a new useAudioDucking hook with the new key as an argument. See useAudioDuckingForVocalTrackChange and useAudioDuckingForCCMReview for examples.
 * @param {AudioDuckSource} id // An id for the ducking behavior.
 *      This must match with a key in the object AUDIO_DUCK_SOURCES found in src/utility/constants
 * @param {boolean} on // whether the ducking should begin to start
 * @param {boolean} off // whether the ducking should begin to turn off
 */
export function useAudioDucking(
	id: AudioDuckSource,
	on: boolean,
	off: boolean
) {
	const localDispatch = useLocalDispatch()
	const localState = useLocalState()

	const audioDuckingValue = getAudioDuckingValue(localState, id)
	const [shouldDuckAudio, setShouldDuckAudio] = useState(on)
	// Since this function is a dependency for a useEffect, react recommends wrapping in a useCallback)
	const updateAudioDucking = useCallback(
		(value: number | null) => {
			localDispatch(updateAudioDuckingValue(value, id))
		},
		[localDispatch, id]
	)

	const lastIntervalRunTime: { current: ?number } = useRef(null)
	let IntervalDelay = AUDIO_DUCKING_INTERVAL
	const intervalIsDone = shouldDuckAudio
		? audioDuckingValue >= AUDIO_DUCK_SOURCES[id].PERCENTAGE
		: audioDuckingValue <= 0

	if (intervalIsDone) {
		// By setting the interval delay to null, the interval will be cleared
		IntervalDelay = null
	}

	useEffect(() => {
		const initialAudioDuckingDiff =
			AUDIO_DUCK_SOURCES[id].PERCENTAGE / audioDuckingIntervalExpectedRunCount
		if (on) {
			setShouldDuckAudio(true)
			updateAudioDucking(initialAudioDuckingDiff)
		} else if (off) {
			setShouldDuckAudio(false)
			updateAudioDucking(-initialAudioDuckingDiff)
		}
	}, [on, off, updateAudioDucking, id])

	useEffect(() => {
		lastIntervalRunTime.current = null
	}, [intervalIsDone])

	useInterval(() => {
		const lastRunTime = lastIntervalRunTime.current
		const timeElapsedSinceLastInterval = lastRunTime
			? Date.now() - lastRunTime
			: AUDIO_DUCKING_INTERVAL
		const percentageOfDelayElapsed =
			timeElapsedSinceLastInterval / AUDIO_DUCKING_DELAY
		const audioDuckingDiff =
			AUDIO_DUCK_SOURCES[id].PERCENTAGE * percentageOfDelayElapsed
		updateAudioDucking(shouldDuckAudio ? audioDuckingDiff : -audioDuckingDiff)
		lastIntervalRunTime.current = Date.now()
	}, IntervalDelay)
}

/**
 * A side effect updating the audio ducking value based on a current vocal track changing.
 * We start an interval that either "ducks" (if there is a new vocal track) or un-"ducks" (if there is no longer a vocal track)
 * the audio over a period of `AUDIO_DUCKING_DELAY`.
 */
export function useAudioDuckingForVocalTrackChange() {
	const currentVocalTrack = useMissionStateSelector(
		getCurrentVocalTrackFromMission
	)
	const previousVocalTrack = usePrevious(currentVocalTrack)

	useAudioDucking(
		'VOCAL_TRACK',
		Boolean(!previousVocalTrack && currentVocalTrack),
		Boolean(previousVocalTrack && !currentVocalTrack)
	)
}

/**
 * A side effect updating the audio ducking value based on a CCM review screen.
 * We start ducking the audio (turning down the audio) if we are grading a CCM or un-"duck" (turn the volume back up) if we are not grading a CCM.
 */
export function useAudioDuckingForCCMReview() {
	const currentScreen = useMissionStateSelector(getScreenFromMission)
	const isGradingCCM =
		currentScreen?.type === 'COLLABORATIVE_CULMINATING_MOMENT_SCREEN' &&
		(currentScreen.currentStep.status ===
			COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.GRADING_UNHIDDEN ||
			currentScreen.currentStep.status ===
				COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.GRADING_HIDDEN ||
			currentScreen.currentStep.status ===
				COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.GRADING_ALL)
	const wasGradingCCM = usePrevious(isGradingCCM)

	useAudioDucking(
		'CCM_REVIEW',
		Boolean(isGradingCCM && !wasGradingCCM),
		Boolean(!isGradingCCM && wasGradingCCM)
	)
}

/**
 * Runs side effects based on changes made in the local state of the player
 */
export function useSideEffects() {
	// Side effect when vocal track changes
	useAudioDuckingForVocalTrackChange()
	useAudioDuckingForCCMReview()
}
